import type { FC, ReactNode } from 'react'
import type { LText } from '@/lib/localized'
import type { ProductDetailData, StoreSettings, VariantData } from '@/types'

import { useMemo, useState } from 'react'
import { BadgePercent, Check, CircleAlert, Layers, MessageCircle, ShoppingBag, TrendingDown, Truck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCart } from '@/lib/cart'
import { useI18n } from '@/lib/i18n'
import { formatAmount } from '@/lib/format'
import { priceLine } from '@/lib/pricing'
import { whatsappLink } from '@/lib/phone'
import { unitLabel } from '@/constants/units'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CmsText } from '@/components/store/cms-text'
import { Countdown } from '@/components/store/countdown'
import { Num, Price } from '@/components/store/price'
import { QuantityStepper } from '@/components/store/quantity-stepper'

interface ProductPurchaseProps {
    product: ProductDetailData
    settings: StoreSettings
    serverNow: number
    pageUrl: string
}

interface OptionValue {
    value: string
    label: LText
    variantId: string
    hex: string | null
}

const uniqueOptions = (variants: Array<VariantData>, key: 'size' | 'color'): Array<OptionValue> => {
    const seen = new Map<string, OptionValue>()

    for (const variant of variants) {
        const label = variant[key]

        if (label && !seen.has(label.v)) seen.set(label.v, { value: label.v, label, variantId: variant.id, hex: key === 'color' ? variant.colorHex : null })
    }

    return [...seen.values()]
}

export const ProductPurchase: FC<ProductPurchaseProps> = ({ product, settings, serverNow, pageUrl }): ReactNode => {
    const { tx, txn, locale } = useI18n()
    const { addItem, open } = useCart()

    const variants = product.variants
    const hasVariants = variants.length > 0
    const sizes = useMemo(() => uniqueOptions(variants, 'size'), [variants])
    const colors = useMemo(() => uniqueOptions(variants, 'color'), [variants])
    const rules = useMemo(() => product.offers.map(offer => offer.rule), [product.offers])
    const initial = variants.find(variant => variant.available) ?? variants[0] ?? null

    const [size, setSize] = useState<string | null>(initial?.size?.v ?? null)
    const [color, setColor] = useState<string | null>(initial?.color?.v ?? null)
    const [qty, setQty] = useState(Math.max(1, product.minOrderQty))

    const matchVariant = (nextSize: string | null, nextColor: string | null): VariantData | null =>
        variants.find(variant => (variant.size?.v ?? null) === nextSize && (variant.color?.v ?? null) === nextColor) ?? null

    const variant = hasVariants ? matchVariant(size, color) : null
    const selectedSize = sizes.find(option => option.value === size) ?? null
    const selectedColor = colors.find(option => option.value === color) ?? null

    const chooseSize = (value: string): void => {
        setSize(value)

        if (!matchVariant(value, color)) {
            const fallback = variants.find(entry => entry.size?.v === value && entry.available) ?? variants.find(entry => entry.size?.v === value)

            setColor(fallback?.color?.v ?? null)
        }
    }

    const chooseColor = (value: string): void => {
        setColor(value)

        if (!matchVariant(size, value)) {
            const fallback = variants.find(entry => entry.color?.v === value && entry.available) ?? variants.find(entry => entry.color?.v === value)

            setSize(fallback?.size?.v ?? null)
        }
    }

    const optionAvailable = (key: 'size' | 'color', value: string): boolean =>
        variants.some(entry => entry[key]?.v === value && entry.available)

    const base = variant
        ? { retail: variant.retail, wholesale: variant.wholesale, wholesaleMinQty: product.wholesaleMinQty }
        : { retail: product.pricing.retail, wholesale: product.pricing.wholesale, wholesaleMinQty: product.wholesaleMinQty }

    const stock = hasVariants ? (variant ? variant.stock : 0) : product.stock
    const maxQty = product.trackStock ? stock : null
    const available = hasVariants ? Boolean(variant?.available) : product.inStock
    const line = priceLine(base, qty, { productId: product.id, categoryId: product.category?.id ?? null }, rules, serverNow)
    const unit = unitLabel(product.unit, locale)
    const exceedsStock = maxQty !== null && qty > maxQty
    const canAdd = available && (!hasVariants || variant !== null) && !exceedsStock

    const quickQuantities = useMemo(() => {
        const list = new Set<number>([Math.max(1, product.minOrderQty)])

        if (line.wholesaleAvailable) {
            list.add(product.wholesaleMinQty)
            list.add(product.wholesaleMinQty * 2)
        }

        product.offers.forEach(offer => {
            if (offer.rule.minQty > 1) list.add(offer.rule.minQty)
        })

        return [...list].filter(value => value >= product.minOrderQty).sort((a, b) => a - b).slice(0, 4)
    }, [line.wholesaleAvailable, product.minOrderQty, product.wholesaleMinQty, product.offers])

    const add = (): void => {
        if (!canAdd) return

        addItem({ productId: product.id, variantId: variant?.id ?? null, qty })
        open()
    }

    const variantText = [variant?.size?.v, variant?.color?.v].filter(Boolean).join(' · ')

    const whatsappMessage = [
        tx('مرحباً، أود الاستفسار عن هذا المنتج:', 'Hello, I would like to ask about this product:'),
        `${product.name.v}${variantText ? ` (${variantText})` : ''}`,
        `${tx('الكمية', 'Quantity')}: ${formatAmount(qty)}`,
        pageUrl
    ].filter(Boolean).join('\n')

    const retailActive = line.tier === 'retail'

    return (
        <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-3 rounded-3xl bg-white p-5 ring-1 ring-navy-100 sm:p-6'>
                <div className='flex flex-wrap items-end justify-between gap-3'>
                    <div className='flex flex-col gap-1'>
                        <span className='text-xs font-medium text-muted-foreground'>
                            {line.tier === 'wholesale' ? tx('سعر الوحدة بالجملة', 'Wholesale unit price') : tx('سعر الوحدة', 'Unit price')}
                        </span>
                        <div className='flex flex-wrap items-baseline gap-2'>
                            <Price value={line.finalUnit} className='text-3xl font-bold text-navy-950 sm:text-4xl' />
                            {line.finalUnit < line.tierUnit && <Price value={line.tierUnit} strike className='text-lg text-navy-400' />}
                            <span className='text-sm text-muted-foreground'>
                                {'/ '}
                                {unit}
                            </span>
                        </div>
                    </div>

                    {line.tier === 'wholesale'
                        ? <Badge tone='cyan' className='text-sm'><Check className='size-3.5' aria-hidden='true' />{tx('سعر الجملة مُطبّق', 'Wholesale price applied')}</Badge>
                        : line.offer && <Badge tone='amber' className='text-sm'><BadgePercent className='size-3.5' aria-hidden='true' />{tx('عرض مُطبّق', 'Offer applied')}</Badge>}
                </div>

                <div className='grid grid-cols-2 gap-2'>
                    <div className={cn('flex flex-col gap-1 rounded-2xl p-3.5 ring-1 transition-colors', retailActive ? 'bg-navy-900 text-white ring-navy-900' : 'bg-navy-50/70 text-navy-900 ring-navy-100')}>
                        <span className={cn('text-xs font-medium', retailActive ? 'text-white/70' : 'text-navy-500')}>
                            {tx('قطاعي', 'Retail')}
                            {line.wholesaleAvailable && (
                                <>
                                    {' · '}
                                    {txn('أقل من {n}', 'under {n}', { n: <Num value={product.wholesaleMinQty} /> })}
                                </>
                            )}
                        </span>
                        <Price value={base.retail} className='text-lg font-bold' />
                    </div>

                    {line.wholesaleAvailable && line.wholesaleUnit !== null
                        ? (
                            <div className={cn('flex flex-col gap-1 rounded-2xl p-3.5 ring-1 transition-colors', !retailActive ? 'bg-navy-900 text-white ring-navy-900' : 'bg-cyan-50 text-navy-900 ring-cyan-100')}>
                                <span className={cn('text-xs font-medium', !retailActive ? 'text-white/70' : 'text-cyan-800')}>
                                    {tx('جملة', 'Wholesale')}
                                    {' · '}
                                    {txn('من {n}', 'from {n}', { n: <Num value={product.wholesaleMinQty} /> })}
                                    {' '}
                                    {unitLabel(product.unit, locale, product.wholesaleMinQty)}
                                </span>
                                <span className='flex flex-wrap items-baseline gap-2'>
                                    <Price value={line.wholesaleUnit} className='text-lg font-bold' />
                                    {base.retail > 0 && line.wholesaleUnit < base.retail && (
                                        <span className={cn('text-xs font-semibold', !retailActive ? 'text-cyan-300' : 'text-emerald-700')}>
                                            {tx('وفّر', 'Save')}
                                            {' '}
                                            <span dir='ltr'>{Math.round(((base.retail - line.wholesaleUnit) / base.retail) * 100)}%</span>
                                        </span>
                                    )}
                                </span>
                            </div>
                        )
                        : (
                            <div className='flex flex-col justify-center gap-1 rounded-2xl bg-navy-50/70 p-3.5 text-xs text-navy-600 ring-1 ring-navy-100'>
                                <Layers className='size-4 text-navy-400' aria-hidden='true' />
                                {tx('سعر موحّد لكل الكميات', 'One price for every quantity')}
                            </div>
                        )}
                </div>
            </div>

            {product.offers.length > 0 && (
                <ul className='flex flex-col gap-2'>
                    {product.offers.map(offer => (
                        <li key={offer.id} className='flex flex-col gap-2 rounded-2xl bg-amber-50/70 p-4 ring-1 ring-amber-100 sm:flex-row sm:items-center sm:justify-between'>
                            <div className='flex items-start gap-3'>
                                <BadgePercent className='mt-0.5 size-5 shrink-0 text-amber-600' aria-hidden='true' />
                                <div className='flex flex-col gap-0.5'>
                                    <CmsText table='offers' id={offer.id} field={offer.title} className='text-sm font-semibold text-navy-950' />
                                    {offer.rule.minQty > 1 && (
                                        <span className='text-xs text-navy-600'>
                                            {txn('عند شراء {n} أو أكثر', 'When you buy {n} or more', { n: <Num value={offer.rule.minQty} className='font-semibold' /> })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {offer.rule.endsAt && <Countdown target={offer.rule.endsAt} compact className='text-amber-800' />}
                        </li>
                    ))}
                </ul>
            )}

            {sizes.length > 0 && (
                <fieldset className='flex flex-col gap-3'>
                    <legend className='mb-3 text-sm font-semibold text-navy-900'>
                        {tx('المقاس', 'Size')}
                        {selectedSize && (
                            <>
                                {': '}
                                <CmsText table='product_variants' id={selectedSize.variantId} field={selectedSize.label} className='font-normal text-navy-600' />
                            </>
                        )}
                    </legend>
                    <div className='flex flex-wrap gap-2'>
                        {sizes.map(option => {
                            const selected = option.value === size
                            const enabled = optionAvailable('size', option.value)

                            return (
                                <button
                                    key={option.value}
                                    type='button'
                                    onClick={() => chooseSize(option.value)}
                                    aria-pressed={selected}
                                    className={cn(
                                        'h-11 min-w-16 rounded-2xl px-4 text-sm font-semibold transition-all',
                                        selected ? 'bg-navy-900 text-white shadow-sm' : 'bg-white text-navy-900 ring-1 ring-navy-200 hover:ring-navy-400',
                                        !enabled && 'text-navy-300 line-through decoration-navy-300'
                                    )}
                                >
                                    <CmsText table='product_variants' id={option.variantId} field={option.label} />
                                </button>
                            )
                        })}
                    </div>
                </fieldset>
            )}

            {colors.length > 0 && (
                <fieldset className='flex flex-col gap-3'>
                    <legend className='mb-3 text-sm font-semibold text-navy-900'>
                        {tx('اللون', 'Color')}
                        {selectedColor && (
                            <>
                                {': '}
                                <CmsText table='product_variants' id={selectedColor.variantId} field={selectedColor.label} className='font-normal text-navy-600' />
                            </>
                        )}
                    </legend>
                    <div className='flex flex-wrap gap-2'>
                        {colors.map(option => {
                            const selected = option.value === color
                            const enabled = optionAvailable('color', option.value)

                            return (
                                <button
                                    key={option.value}
                                    type='button'
                                    onClick={() => chooseColor(option.value)}
                                    aria-pressed={selected}
                                    className={cn(
                                        'flex h-11 items-center gap-2 rounded-2xl ps-2 pe-4 text-sm font-medium transition-all',
                                        selected ? 'bg-navy-900 text-white shadow-sm' : 'bg-white text-navy-900 ring-1 ring-navy-200 hover:ring-navy-400',
                                        !enabled && 'opacity-50'
                                    )}
                                >
                                    <span className='size-7 rounded-xl ring-1 ring-inset ring-black/10' style={{ backgroundColor: option.hex ?? '#e3e8f1' }} aria-hidden='true' />
                                    <CmsText table='product_variants' id={option.variantId} field={option.label} />
                                </button>
                            )
                        })}
                    </div>
                </fieldset>
            )}

            <div className='flex flex-col gap-3'>
                <div className='flex flex-wrap items-center justify-between gap-3'>
                    <p className='text-sm font-semibold text-navy-900'>{tx('الكمية', 'Quantity')}</p>

                    {product.minOrderQty > 1 && (
                        <span className='text-xs text-muted-foreground'>
                            {txn('أقل كمية للطلب {n}', 'Minimum order {n}', { n: <Num value={product.minOrderQty} className='font-semibold text-navy-900' /> })}
                        </span>
                    )}
                </div>

                <div className='flex flex-wrap items-center gap-2'>
                    <QuantityStepper value={qty} onChange={setQty} min={Math.max(1, product.minOrderQty)} max={maxQty} size='lg' label={tx('الكمية', 'Quantity')} />

                    {quickQuantities.map(value => (
                        <button
                            key={value}
                            type='button'
                            onClick={() => setQty(maxQty !== null ? Math.min(value, Math.max(maxQty, product.minOrderQty)) : value)}
                            className={cn('h-10 rounded-full px-3.5 text-sm font-semibold tabular-nums transition-colors', qty === value ? 'bg-cyan-500 text-navy-950' : 'bg-navy-50 text-navy-800 hover:bg-navy-100')}
                        >
                            {formatAmount(value)}
                        </button>
                    ))}
                </div>

                {line.qtyToWholesale > 0 && line.wholesaleUnit !== null && (
                    <button type='button' onClick={() => setQty(product.wholesaleMinQty)} className='flex items-center gap-2.5 rounded-2xl bg-cyan-50 px-4 py-3 text-start text-sm text-cyan-900 ring-1 ring-cyan-100 transition-colors hover:bg-cyan-100'>
                        <TrendingDown className='size-4.5 shrink-0 text-cyan-700' aria-hidden='true' />
                        <span>
                            {txn('أضف {n} للحصول على سعر الجملة {price} للوحدة', 'Add {n} more to get the wholesale price of {price} each', {
                                n: <strong className='font-semibold'><Num value={line.qtyToWholesale} /></strong>,
                                price: <strong className='font-semibold'><Price value={line.wholesaleUnit} /></strong>
                            })}
                        </span>
                    </button>
                )}
            </div>

            <div className='flex flex-col gap-4 rounded-3xl bg-surface p-5 ring-1 ring-navy-100'>
                <div className='flex flex-col gap-2 text-sm'>
                    <div className='flex items-center justify-between text-navy-700'>
                        <span>
                            <Num value={qty} />
                            {' × '}
                            <Price value={line.tierUnit} />
                        </span>
                        <Price value={line.subtotal} />
                    </div>

                    {line.discount > 0 && (
                        <div className='flex items-center justify-between text-emerald-700'>
                            <span>{tx('خصم العرض', 'Offer discount')}</span>
                            <span className='inline-flex items-center gap-1 font-medium'><span aria-hidden='true'>-</span><Price value={line.discount} /></span>
                        </div>
                    )}

                    {line.wholesaleSavings > 0 && (
                        <div className='flex items-center justify-between text-emerald-700'>
                            <span>{tx('توفيرك بسعر الجملة', 'Your wholesale saving')}</span>
                            <Price value={line.wholesaleSavings} className='font-medium' />
                        </div>
                    )}

                    <div className='mt-1 flex items-center justify-between border-t border-navy-100 pt-3 text-base font-semibold text-navy-950'>
                        <span>{tx('الإجمالي', 'Total')}</span>
                        <Price value={line.total} className='text-xl font-bold' />
                    </div>
                </div>

                {!available && (
                    <p className='flex items-center gap-2 text-sm font-medium text-red-600' role='alert'>
                        <CircleAlert className='size-4' aria-hidden='true' />
                        {hasVariants && !variant ? tx('هذا الاختيار غير متاح، اختر مقاساً أو لوناً آخر.', 'This combination is not available. Choose another option.') : tx('نفدت الكمية حالياً.', 'Currently out of stock.')}
                    </p>
                )}

                {available && exceedsStock && maxQty !== null && (
                    <p className='flex items-center gap-2 text-sm font-medium text-red-600' role='alert'>
                        <CircleAlert className='size-4' aria-hidden='true' />
                        {txn('المتاح حالياً {n} فقط.', 'Only {n} available right now.', { n: <Num value={maxQty} /> })}
                    </p>
                )}

                {available && !exceedsStock && maxQty !== null && maxQty <= 20 && (
                    <p className='text-xs font-medium text-amber-700'>
                        {txn('باقي {n} فقط في المخزون', 'Only {n} left in stock', { n: <Num value={maxQty} /> })}
                    </p>
                )}

                <div className='flex flex-col gap-2 sm:flex-row'>
                    <Button size='lg' className='flex-1' onClick={add} disabled={!canAdd}>
                        <ShoppingBag className='size-5' aria-hidden='true' />
                        {tx('أضف إلى السلة', 'Add to cart')}
                    </Button>

                    {settings.whatsapp && (
                        <a href={whatsappLink(settings.whatsapp, whatsappMessage)} target='_blank' rel='noopener noreferrer' className='inline-flex h-13 items-center justify-center gap-2 rounded-full bg-white px-6 text-base font-medium text-navy-900 ring-1 ring-navy-200 transition-colors hover:bg-navy-50'>
                            <MessageCircle className='size-5 text-[#1fa855]' aria-hidden='true' />
                            {tx('استفسر على واتساب', 'Ask on WhatsApp')}
                        </a>
                    )}
                </div>

                <p className='flex items-center gap-2 text-xs text-muted-foreground'>
                    <Truck className='size-4 shrink-0 text-cyan-700' aria-hidden='true' />
                    {settings.freeShippingEnabled && settings.freeShippingThreshold > 0
                        ? txn('شحن لكل المحافظات، ومجاني للطلبات فوق {amount}', 'Shipping to every governorate, free on orders over {amount}', { amount: <Price value={settings.freeShippingThreshold} className='font-semibold text-navy-800' /> })
                        : tx('شحن لكل المحافظات، وتظهر التكلفة قبل إتمام الطلب', 'Shipping to every governorate, with the fee shown before you order')}
                </p>
            </div>
        </div>
    )
}
